import React, { createContext, useContext, useState, useEffect } from 'react';
import { getCompanyBySlug, getActiveCompanies } from '../firebase/companies';

const TenantContext = createContext();

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

// Utility to detect tenant from hostname
const getTenantFromHostname = () => {
  const hostname = window.location.hostname;
  
  // Production: abc.carbooking.com
  if (hostname.includes('.') && !hostname.startsWith('www.') && !hostname.startsWith('localhost')) {
    const parts = hostname.split('.');
    // Check if it's a subdomain (e.g., abc.carbooking.com)
    if (parts.length >= 3 || (parts.length >= 2 && !['com', 'in', 'org', 'net'].includes(parts[parts.length - 1]))) {
      const subdomain = parts[0];
      // Exclude common subdomains
      if (!['www', 'app', 'admin', 'api'].includes(subdomain)) {
        return subdomain;
      }
    }
  }
  
  // Local development: abc.localhost:3000
  if (hostname.endsWith('.localhost') || hostname.includes('.localhost:')) {
    const subdomain = hostname.split('.')[0];
    return subdomain;
  }
  
  // Also check for abc.localhost without port
  if (hostname.includes('.localhost')) {
    return hostname.split('.')[0];
  }
  
  // Fallback: Query param ?company=abc (for testing)
  const urlParams = new URLSearchParams(window.location.search);
  const companyParam = urlParams.get('company');
  if (companyParam) {
    return companyParam;
  }
  
  return null;
};

export const TenantProvider = ({ children }) => {
  const [tenant, setTenant] = useState(null); // Company slug
  const [company, setCompany] = useState(null); // Full company data
  const [companies, setCompanies] = useState([]); // All active companies (for main site)
  const [isLoading, setIsLoading] = useState(true);
  const [isMainSite, setIsMainSite] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const initializeTenant = async () => {
      try {
        const slug = getTenantFromHostname();
        
        if (slug) {
          // Subdomain detected - fetch company data
          setTenant(slug);
          const result = await getCompanyBySlug(slug);
          
          if (result.company) {
            if (result.company.status === 'active') {
              setCompany(result.company);
              setIsMainSite(false);
            } else {
              setError('This company is currently inactive.');
            }
          } else {
            setError('Company not found.');
          }
        } else {
          // Main site - no subdomain
          setIsMainSite(true);
          // Load all active companies for company selector (with timeout)
          try {
            const result = await Promise.race([
              getActiveCompanies(),
              new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 5000))
            ]);
            if (result.companies) {
              setCompanies(result.companies);
            }
          } catch (companyError) {
            console.warn('Could not load companies:', companyError);
            setCompanies([]);
          }
        }
      } catch (err) {
        console.error('Error initializing tenant:', err);
        // Don't set error for main site - just continue with empty companies
        const currentSlug = getTenantFromHostname();
        if (currentSlug) {
          setError('Failed to load company data.');
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    initializeTenant();
  }, []);

  // Function to manually set tenant (for testing or admin)
  const setTenantManually = async (slug) => {
    if (!slug) {
      setTenant(null);
      setCompany(null);
      setIsMainSite(true);
      return;
    }

    setIsLoading(true);
    try {
      const result = await getCompanyBySlug(slug);
      if (result.company && result.company.status === 'active') {
        setTenant(slug);
        setCompany(result.company);
        setIsMainSite(false);
        setError(null);
      } else {
        setError(result.company ? 'Company is inactive.' : 'Company not found.');
      }
    } catch (err) {
      setError('Failed to load company data.');
    } finally {
      setIsLoading(false);
    }
  };

  // Refresh company data
  const refreshCompany = async () => {
    if (!tenant) return;
    
    const result = await getCompanyBySlug(tenant);
    if (result.company) {
      setCompany(result.company);
    }
  };

  const value = {
    tenant,           // Company slug
    company,          // Full company data from Firestore
    companies,        // All active companies (for main site selector)
    isLoading,
    isMainSite,       // True if on main site (no tenant)
    error,
    setTenantManually,
    refreshCompany,
    // Computed values
    companyId: company?.id || null,
    companyName: company?.name || 'CarBooking',
    companyLogo: company?.logo || null,
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

export default TenantContext;
